import { useMemo, useState } from 'react';
import { useUrlState } from '@/hooks/useUrlState';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { IconButton } from '@liminal/web-ui/primitives/IconButton';
import { Select } from '@liminal/web-ui/forms/Select';
import { Button } from '@liminal/web-ui/primitives/Button';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { SortableTableCell } from '@liminal/web-ui/display/SortableTableCell';
import { useTableSort } from '@liminal/web-ui/primitives/useTableSort';
import { Snackbar } from '@liminal/web-ui/feedback/Snackbar';
import { useSnackbar } from '@liminal/web-ui/feedback/useSnackbar';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import {
  useDeleteFFlagOverride,
  useFFlags,
  useFFlagOverrides,
  useSetFFlagOverride,
  useUserProfiles,
} from '@/hooks/useFFlags';
import type { FFlagOverride } from '@/api/types';
import { FONT_STACK_BODY } from '@liminal/brand/font-faces';
import { SearchInput } from '@liminal/web-ui/forms/SearchInput';
import { getErrorMessage } from '@/errors/messages';

const VALUE_OPTIONS = [
  { value: '', label: 'All values' },
  { value: 'enabled', label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
];

const COLUMN_COUNT = 6;

const FILTER_DEFAULTS = { q: '', flag: '', value: '' };

export function FFlagOverridesPage() {
  const navigate = useNavigate();

  const [{ q: search, flag: flagFilter, value: valueFilter }, setParams] =
    useUrlState(FILTER_DEFAULTS);

  const { data, isLoading, isError } = useFFlagOverrides({
    fflagName: flagFilter || undefined,
  });
  const { data: flagsData } = useFFlags();
  const setMutation = useSetFFlagOverride();
  const deleteMutation = useDeleteFFlagOverride();
  const snackbar = useSnackbar();
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  // `?? []` mints a new array every render, so any useMemo/useCallback
  // depending on it re-runs every render. Memoise the fallback itself.
  const overrides: FFlagOverride[] = useMemo(() => data?.overrides ?? [], [data]);
  const flags = useMemo(() => flagsData?.fflags ?? [], [flagsData]);
  // The filter's "any" choice is the empty value, as the shared Select's
  // `filter` variant expects (common/js/web-ui/src/forms/Select.tsx).
  const flagOptions = useMemo(
    () => [{ value: '', label: 'All flags' }, ...flags.map((f) => ({ value: f.name, label: f.name }))],
    [flags],
  );
  const profileMap = useUserProfiles(overrides.map((o) => o.userId));

  const overrideKey = (o: FFlagOverride) => `${o.fflagName}::${o.userId}`;

  const filteredOverrides = useMemo(() => {
    const q = search.trim().toLowerCase();
    return overrides.filter((o) => {
      if (valueFilter === 'enabled' && !o.enabled) return false;
      if (valueFilter === 'disabled' && o.enabled) return false;
      if (!q) return true;
      const profile = profileMap.get(o.userId);
      const haystack = [
        profile?.email,
        profile?.firstName,
        profile?.lastName,
        o.fflagName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [overrides, profileMap, search, valueFilter]);

  // Not module scope like the other tables: three of these six columns render
  // a profile this page resolves separately, so the accessors close over that
  // map and have to be rebuilt when it does.
  const sortAccessors = useMemo(
    () => ({
      email: (o: FFlagOverride) => profileMap.get(o.userId)?.email,
      firstName: (o: FFlagOverride) => profileMap.get(o.userId)?.firstName,
      lastName: (o: FFlagOverride) => profileMap.get(o.userId)?.lastName,
      fflagName: (o: FFlagOverride) => o.fflagName,
      enabled: (o: FFlagOverride) => o.enabled ?? false,
    }),
    [profileMap],
  );

  // Every override is already on the page, so this sorts all of them.
  const { rows: sortedOverrides, sort } = useTableSort(
    filteredOverrides,
    sortAccessors,
    'fflagName',
    'asc',
  );

  const handleToggle = async (o: FFlagOverride) => {
    const key = overrideKey(o);
    setPendingKey(key);
    try {
      await setMutation.mutateAsync({ name: o.fflagName, userId: o.userId, enabled: !o.enabled });
      snackbar.show(`Updated ${o.fflagName}`);
    } catch (e) {
      snackbar.show(getErrorMessage(e), 'error');
    } finally {
      setPendingKey(null);
    }
  };

  const handleDelete = async (o: FFlagOverride) => {
    const key = overrideKey(o);
    setPendingKey(key);
    try {
      await deleteMutation.mutateAsync({ name: o.fflagName, userId: o.userId });
      snackbar.show(`Deleted override for ${o.fflagName}`);
    } catch (e) {
      snackbar.show(getErrorMessage(e), 'error');
    } finally {
      setPendingKey(null);
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          Per-user overrides take precedence over the global flag state.
        </Typography>
        <Button
          variant="primary"
          startIcon="plus"
          onClick={() => navigate('/fflags/users/new')}
        >
          Add Override
        </Button>
      </Stack>

      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
        <SearchInput
          value={search}
          onChange={(value) => setParams({ q: value })}
          placeholder="Search email or name..."
          width={300}
        />
        <Select
          variant="filter"
          aria-label="Flag"
          size="sm"
          width={220}
          value={flagFilter}
          onChange={(flag) => setParams({ flag })}
          options={flagOptions}
        />
        <Select
          variant="filter"
          aria-label="Value"
          size="sm"
          width={160}
          value={valueFilter}
          onChange={(value) => setParams({ value })}
          options={VALUE_OPTIONS}
        />
        {!isLoading && (
          <Typography variant="body2" color="text.secondary">
            {filteredOverrides.length} of {overrides.length}
          </Typography>
        )}
      </Stack>

      <Card variant="elevated" padding="none">
        <TableContainer>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <SortableTableCell column="email" sort={sort}>
                  Email
                </SortableTableCell>
                <SortableTableCell column="firstName" sort={sort}>
                  First Name
                </SortableTableCell>
                <SortableTableCell column="lastName" sort={sort}>
                  Last Name
                </SortableTableCell>
                <SortableTableCell column="fflagName" sort={sort}>
                  Flag
                </SortableTableCell>
                <SortableTableCell column="enabled" sort={sort}>
                  Value
                </SortableTableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <DataTableBody
              columns={COLUMN_COUNT}
              isLoading={isLoading}
              isError={isError}
              isEmpty={sortedOverrides.length === 0}
              emptyMessage={
                overrides.length === 0
                  ? 'No user overrides found.'
                  : 'No overrides match the current filters.'
              }
              errorMessage="Could not load feature flag overrides."
            >
              {sortedOverrides.map((o) => {
                      const key = overrideKey(o);
                      const profile = profileMap.get(o.userId);
                      const isPending = pendingKey === key;
                      return (
                        <TableRow key={key} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {profile?.email ?? '…'}
                            </Typography>
                          </TableCell>
                          <TableCell>{profile?.firstName ?? '—'}</TableCell>
                          <TableCell>{profile?.lastName ?? '—'}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: FONT_STACK_BODY }}>
                              {o.fflagName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <StatusPill
                              label={o.enabled ? 'Enabled' : 'Disabled'}
                              tone={o.enabled ? 'success' : 'neutral'}
                              size="sm"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                              <Button
                                size="sm"
                                variant={o.enabled ? 'secondary' : 'primary'}
                                loading={isPending && setMutation.isPending}
                                disabled={pendingKey !== null && !isPending}
                                onClick={() => handleToggle(o)}
                              >
                                {o.enabled ? 'Disable' : 'Enable'}
                              </Button>
                              <IconButton
                                icon="trash"
                                label="Delete override"
                                size="sm"
                                tone="danger"
                                disabled={pendingKey !== null && !isPending}
                                onClick={() => handleDelete(o)}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                );
              })}
            </DataTableBody>
          </Table>
        </TableContainer>
      </Card>

      <Snackbar {...snackbar.snackbarProps} />
    </Stack>
  );
}
